import { getCSRFToken } from '../utils/getCSRFToken';

export default function registerPackageBuilderStore() {
  const store = Alpine.store('packageBuilderStore', {
    // Current step in the form
    currentStep: 1,
    totalSteps: 4,

    // Package basic information (only fields used by builder)
    packageData: {
      public_name: '',
      is_fixed: false, // Default to flexible pricing
    },

    // Pricing mode selection
    pricingMode: '', // 'PACKAGE_DEFINED' or 'COMPONENT_SUM'

    // Primary component
    primaryComponent: null,
    primaryComponentConfig: {
      order: 0,
      showsOnBreakdown: false,
      ignoreListingBlockedDates: false, // COMPONENT_SUM only
      revenueAllocation: null, // For package defined pricing
      pricingMode: 'OWN_RATES', // 'INCLUDED', 'OWN_RATES', 'OWN_RATES_DISCOUNTED'
      discountType: 'PERCENTAGE', // 'PERCENTAGE' or 'FIXED'
      discount: 0, // 0 = full price, 100 = free
      discountCondition: 'ALWAYS', // 'ALWAYS', 'LENGTH_OF_STAY', 'EARLY_BIRD', 'LAST_MINUTE', 'WEEKDAY'
      discountMinDays: null,
      discountMaxDays: null,
      discountDaysBefore: null,
      discountWeekdays: [],
      peopleInfluencePrice: false, // Synced from primary component's people_influence_price
      usePersonTypes: false, // Synced from primary component's use_person_types
    },

    // Secondary components
    secondaryComponents: [],
    secondaryExtras: [],

    // Helper to get next order number for secondary components
    getNextOrderNumber() {
      return (
        Math.max(0, ...this.secondaryComponents.map((comp) => comp.order)) + 1
      );
    },

    // Calculate total revenue allocation
    getTotalRevenueAllocation() {
      let total = 0;

      // Add primary component allocation
      if (this.primaryComponentConfig.revenueAllocation) {
        total += parseFloat(this.primaryComponentConfig.revenueAllocation) || 0;
      }

      // Add secondary components allocation
      this.secondaryComponents.forEach((comp) => {
        if (comp.revenueAllocation) {
          total += parseFloat(comp.revenueAllocation) || 0;
        }
      });

      // Add secondary extras allocation
      this.secondaryExtras.forEach((extraComp) => {
        if (extraComp.active && extraComp.revenueAllocation) {
          total += parseFloat(extraComp.revenueAllocation) || 0;
        }
      });

      return total;
    },

    // Form state
    isLoading: false,
    errors: {},
    showSuccessModal: false,
    successData: null,
    hasCreated: false,
    createdPackageId: null,

    // Available rate sets (passed from backend)
    availableRateSets: [],
    availableExtras: [],

    // Helper method to check if primary component allows "One per person" option
    isPrimaryComponentPersonBased() {
      if (!this.primaryComponent) {
        console.log('No primary component selected');
        return false;
      }

      // Check if the primary component has person-based unit types
      const unitType =
        this.primaryComponent.accommodation_type ||
        this.primaryComponent.tour_type;
      if (!unitType) {
        console.log('Primary component has no unit type');
        return false;
      }

      // Check if unit type is person-based (Person, Adult, Child, etc.)
      const personBasedTypes = [
        'person',
        'people',
        'adult',
        'child',
        'adults',
        'children',
      ];
      const peopleAsOption = this.primaryComponent.people_as_option;

      const isPersonBased =
        personBasedTypes.some((type) =>
          unitType.toLowerCase().includes(type.toLowerCase()),
        ) || peopleAsOption === true;

      console.log(
        `Primary component unit type: ${unitType}, people_as_option: ${peopleAsOption}, is person-based: ${isPersonBased}`,
      );
      return isPersonBased;
    },

    // Helper method to check if a secondary component can use "One per person"
    canUsePerPersonForSecondary(component) {
      const canUse = this.isPrimaryComponentPersonBased();
      console.log(`Can use PER_PERSON for secondary component: ${canUse}`);
      return canUse;
    },

    // Computed properties to separate listings by type
    get accommodationRateSets() {
      return this.availableRateSets.filter(
        (rateSet) => rateSet.accommodation_type && !rateSet.tour_type,
      );
    },

    get tourRateSets() {
      return this.availableRateSets.filter(
        (rateSet) => rateSet.tour_type && !rateSet.accommodation_type,
      );
    },

    get accommodationSecondaryRateSets() {
      return this.accommodationRateSets.filter((rateSet) => {
        // Exclude primary component
        if (this.primaryComponent && rateSet.id === this.primaryComponent.id) {
          return false;
        }
        // Exclude already selected secondary components
        const selectedSecondaryIds = this.secondaryComponents.map(
          (comp) => comp.rateSet.id,
        );
        return !selectedSecondaryIds.includes(rateSet.id);
      });
    },

    get tourSecondaryRateSets() {
      return this.tourRateSets.filter((rateSet) => {
        // Exclude primary component
        if (this.primaryComponent && rateSet.id === this.primaryComponent.id) {
          return false;
        }
        // Exclude already selected secondary components
        const selectedSecondaryIds = this.secondaryComponents.map(
          (comp) => comp.rateSet.id,
        );
        return !selectedSecondaryIds.includes(rateSet.id);
      });
    },

    // Computed property to get available rate sets for secondary selection (excludes primary and already selected secondaries)
    get availableSecondaryRateSets() {
      let filtered = this.availableRateSets;

      // Exclude primary component
      if (this.primaryComponent) {
        filtered = filtered.filter(
          (rateSet) => rateSet.id !== this.primaryComponent.id,
        );
      }

      // Exclude already selected secondary components
      const selectedSecondaryIds = this.secondaryComponents.map(
        (comp) => comp.rateSet.id,
      );
      filtered = filtered.filter(
        (rateSet) => !selectedSecondaryIds.includes(rateSet.id),
      );

      return filtered;
    },

    get availableSecondaryExtras() {
      const selectedExtraIds = this.secondaryExtras.map(
        (comp) => comp.extra.id,
      );
      return this.availableExtras.filter(
        (extra) => !selectedExtraIds.includes(extra.id),
      );
    },

    // Step validation
    stepValidation: {
      1: false, // Basic info + pricing mode
      2: false, // Primary component
      3: false, // Pricing configuration
      4: false, // Secondary components (optional)
    },

    // Initialize the store
    init(availableRateSets = [], availableExtras = []) {
      try {
        this.availableRateSets = Array.isArray(availableRateSets)
          ? availableRateSets
          : [];
        this.availableExtras = Array.isArray(availableExtras)
          ? availableExtras
          : [];
        console.log(
          'Package Builder Store initialized with rate sets:',
          this.availableRateSets,
        );

        // Fix any existing secondary components that might have invalid quantity types
        this.fixExistingSecondaryComponents();

        // Add beforeunload warning to prevent accidental navigation away
        this.setupNavigationWarning();
      } catch (error) {
        console.error('Error initializing package builder store:', error);
        this.availableRateSets = [];
      }
    },

    // Navigation methods
    nextStep() {
      if (this.canProceedToNextStep()) {
        this.currentStep++;
        this.validateCurrentStep();
      }
    },

    // Check if a step is accessible (all previous steps are complete)
    isStepAccessible(step) {
      if (step <= this.currentStep) {
        return true; // Current step or previous steps are always accessible
      }

      // For future steps, check if all previous steps are complete
      for (let i = 1; i < step; i++) {
        if (!this.isStepComplete(i)) {
          return false;
        }
      }
      return true;
    },

    // Method to scroll to navigation buttons
    scrollToNavigation() {
      setTimeout(() => {
        const navigationElement = document.querySelector(
          '.d-flex.justify-content-between.mt-4',
        );
        if (navigationElement) {
          navigationElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      }, 100);
    },

    // Check if there's unsaved progress
    hasUnsavedProgress() {
      // Check if user has made any progress beyond step 1
      return (
        this.currentStep > 1 ||
        this.packageData.public_name.trim() !== '' ||
        this.pricingMode !== '' ||
        this.primaryComponent !== null ||
        this.secondaryComponents.length > 0 ||
        this.secondaryExtras.length > 0
      );
    },

    // Setup navigation warning
    setupNavigationWarning() {
      const beforeUnloadHandler = (event) => {
        if (this.hasUnsavedProgress() && !this.hasCreated) {
          const message =
            'You have unsaved progress in the package builder. Are you sure you want to leave?';
          event.preventDefault();
          event.returnValue = message;
          return message;
        }
      };

      // Add the event listener
      window.addEventListener('beforeunload', beforeUnloadHandler);

      // Store the handler so we can remove it later if needed
      this.beforeUnloadHandler = beforeUnloadHandler;
    },

    // Remove navigation warning (call when package is created successfully)
    removeNavigationWarning() {
      if (this.beforeUnloadHandler) {
        window.removeEventListener('beforeunload', this.beforeUnloadHandler);
        this.beforeUnloadHandler = null;
      }
    },

    previousStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
      }
    },

    goToStep(step) {
      if (step >= 1 && step <= this.totalSteps) {
        this.currentStep = step;
        this.validateCurrentStep();
      }
    },

    // Validation methods
    canProceedToNextStep() {
      switch (this.currentStep) {
        case 1:
          return this.validateStep1();
        case 2:
          return this.validateStep2();
        case 3:
          return this.validateStep3();
        case 4:
          return true; // Step 4 is optional
        default:
          return false;
      }
    },

    validateStep1() {
      const hasBasicInfo = this.packageData.public_name.trim() !== '';
      const hasPricingMode =
        this.pricingMode !== '' && this.pricingMode !== null;

      // For PACKAGE_DEFINED pricing, also check if fixed/flexible is selected
      let hasPricingStructure = true;
      if (this.pricingMode === 'PACKAGE_DEFINED') {
        hasPricingStructure =
          this.packageData.is_fixed !== null &&
          this.packageData.is_fixed !== undefined;
      }

      this.stepValidation[1] =
        hasBasicInfo && hasPricingMode && hasPricingStructure;
      return this.stepValidation[1];
    },

    validateStep2() {
      const hasPrimaryComponent = this.primaryComponent !== null;

      if (!hasPrimaryComponent) {
        this.stepValidation[2] = false;
        return false;
      }

      // For package defined pricing, revenue is now configured in Step 3 allocator banner.
      if (this.pricingMode === 'PACKAGE_DEFINED') {
        this.stepValidation[2] = true;
        return true;
      }

      // For component sum pricing, no additional validation needed (discount is optional)
      this.stepValidation[2] = true;
      return true;
    },

    validateStep3() {
      // For COMPONENT_SUM, this step is automatically valid
      if (this.pricingMode === 'COMPONENT_SUM') {
        this.stepValidation[3] = true;
        return true;
      }

      // For PACKAGE_DEFINED, validate that total revenue allocation equals 100%
      if (this.pricingMode === 'PACKAGE_DEFINED') {
        const totalAllocation = this.getTotalRevenueAllocation();
        const isValid = Math.abs(totalAllocation - 100) < 0.01; // Allow small floating point differences
        this.stepValidation[3] = isValid;
        return isValid;
      }

      this.stepValidation[3] = true;
      return true;
    },

    validateCurrentStep() {
      this.canProceedToNextStep();
    },

    // Component management
    selectPrimaryComponent(rateSet) {
      this.primaryComponent = rateSet;
      this.primaryComponentConfig.order = 0; // Always 0 for primary

      // Initialize people_influence_price from the selected rate set
      this.primaryComponentConfig.peopleInfluencePrice =
        rateSet.people_influence_price || false;

      // Initialize use_person_types from the selected rate set
      this.primaryComponentConfig.usePersonTypes =
        rateSet.use_person_types || false;

      this.validateStep2();
      console.log('Primary component selected:', rateSet);
      console.log(
        'People influence price initialized to:',
        this.primaryComponentConfig.peopleInfluencePrice,
      );
      console.log(
        'Use person types initialized to:',
        this.primaryComponentConfig.usePersonTypes,
      );

      // Check if any secondary components need to have their quantity type updated
      this.updateSecondaryComponentsQuantityType();

      // Scroll to primary component configuration after a short delay
      setTimeout(() => {
        const configElement = document.querySelector('[data-primary-config]');
        if (configElement) {
          configElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      }, 100);
    },

    addSecondaryComponent(rateSet) {
      // Check if already added as secondary
      if (
        this.secondaryComponents.find((comp) => comp.rateSet.id === rateSet.id)
      ) {
        console.warn('Component already added as secondary');
        return;
      }

      // Check if it's the primary component
      if (this.primaryComponent && this.primaryComponent.id === rateSet.id) {
        console.warn('Cannot add primary component as secondary');
        return;
      }

      const secondaryComponent = {
        rateSet: rateSet,
        order: this.getNextOrderNumber(),
        showsOnBreakdown: false,
        ignoreListingBlockedDates: false, // COMPONENT_SUM only
        optional: false,
        quantityType: 'FIXED', // Default to FIXED, will be set to PER_PERSON only if allowed
        fixedQuantity: 1,
        excludeFromStart: 0,
        excludeFromEnd: 0,
        maxDays: 0,
        maxDaysMode: 'FIXED', // 'FIXED' | 'PRIMARY_DURATION_MAP'
        maxDaysPrimaryDurationMap: {},
        maxDaysPrimaryDurationMapJson: '{}',
        maxDaysPrimaryDurationRows: [],
        minPrimaryIntervalDays: null,
        dateApplicationMode: 'RANGE', // 'RANGE', 'START_ONLY', 'END_ONLY', 'START_AND_END'
        addingDirection: 'FROM_START', // 'FROM_START', 'FROM_END'
        weekdayRestrictionActive: false, // Toggle for weekday restrictions
        daysOfWeek: [], // Array of day numbers (0-6)
        revenueAllocation: null, // For package defined pricing
        pricingMode: 'OWN_RATES', // 'INCLUDED', 'OWN_RATES', 'OWN_RATES_DISCOUNTED'
        discountType: 'PERCENTAGE', // 'PERCENTAGE' or 'FIXED'
        discount: 0, // 0 = full price, 100 = free
        discountCondition: 'ALWAYS', // 'ALWAYS', 'LENGTH_OF_STAY', 'EARLY_BIRD', 'LAST_MINUTE', 'WEEKDAY'
        discountMinDays: null,
        discountMaxDays: null,
        discountDaysBefore: null,
        discountWeekdays: [],
      };

      // Set to PER_PERSON only if the primary component allows it
      if (this.canUsePerPersonForSecondary(secondaryComponent)) {
        secondaryComponent.quantityType = 'PER_PERSON';
      }

      this.secondaryComponents.push(secondaryComponent);
      this.validateStep3();
      console.log('Secondary component added:', secondaryComponent);
    },

    getNextExtraOrderNumber() {
      return Math.max(0, ...this.secondaryExtras.map((comp) => comp.order)) + 1;
    },

    addSecondaryExtra(extra) {
      if (this.secondaryExtras.find((comp) => comp.extra.id === extra.id)) {
        console.warn('Extra already added as secondary extra');
        return;
      }

      const secondaryExtra = {
        extra,
        order: this.getNextExtraOrderNumber(),
        quantityType: this.isPrimaryComponentPersonBased()
          ? 'PER_PERSON'
          : 'FIXED',
        fixedQuantity: 1,
        maxQuantity: null,
        minPrimaryIntervalDays: null,
        durationAdjustment: 0,
        revenueAllocation: null, // For package defined pricing
        pricingMode: 'OWN_PRICE', // OWN_PRICE | DISCOUNTED | INCLUDED
        discountType: 'PERCENTAGE',
        discountAmount: 0,
        showsOnBreakdown: true,
        displayName: '',
        active: true,
      };

      this.secondaryExtras.push(secondaryExtra);
      this.validateStep3();
    },

    removeSecondaryExtra(index) {
      this.secondaryExtras.splice(index, 1);
    },

    normalizeSecondaryDateRules(component) {
      const dateApplicationMode = component.dateApplicationMode || 'RANGE';
      const isRangeMode = dateApplicationMode === 'RANGE';

      if (!isRangeMode) {
        component.excludeFromStart = 0;
        component.excludeFromEnd = 0;
        component.maxDays = 0;
        component.maxDaysMode = 'FIXED';
        component.maxDaysPrimaryDurationMap = {};
        component.maxDaysPrimaryDurationMapJson = '{}';
        component.maxDaysPrimaryDurationRows = [];
        component.addingDirection = 'FROM_START';
        component.weekdayRestrictionActive = false;
        component.daysOfWeek = [];
      } else if (!component.addingDirection) {
        component.addingDirection = 'FROM_START';
      }

      return component;
    },

    removeSecondaryComponent(index) {
      this.secondaryComponents.splice(index, 1);
      console.log('Secondary component removed at index:', index);
    },

    addMaxDaysPrimaryDurationRow(component) {
      if (!component.maxDaysPrimaryDurationRows) {
        component.maxDaysPrimaryDurationRows = [];
      }
      component.maxDaysPrimaryDurationRows.push({
        primaryDays: null,
        maxDays: null,
      });
    },

    removeMaxDaysPrimaryDurationRow(component, rowIndex) {
      if (!component.maxDaysPrimaryDurationRows) return;
      component.maxDaysPrimaryDurationRows.splice(rowIndex, 1);
    },

    handleMaxDaysModeChange(component) {
      if (!component) return;
      if (component.maxDaysMode === 'PRIMARY_DURATION_MAP') {
        if (!component.maxDaysPrimaryDurationRows) {
          component.maxDaysPrimaryDurationRows = [];
        }
        if (component.maxDaysPrimaryDurationRows.length === 0) {
          this.addMaxDaysPrimaryDurationRow(component);
        }
      }
    },

    removePrimaryComponent() {
      this.primaryComponent = null;
      this.primaryComponentConfig = {
        order: 0,
        showsOnBreakdown: false,
        ignoreListingBlockedDates: false,
        revenueAllocation: null,
        pricingMode: 'OWN_RATES',
        discountType: 'PERCENTAGE',
        discount: 0,
        discountCondition: 'ALWAYS',
        discountMinDays: null,
        discountMaxDays: null,
        discountDaysBefore: null,
        discountWeekdays: [],
        peopleInfluencePrice: false,
        usePersonTypes: false,
      };
      console.log('Primary component removed');
    },

    // Fix existing secondary components that might have invalid quantity types
    fixExistingSecondaryComponents() {
      this.secondaryComponents.forEach((component) => {
        // If component is using "PER_PERSON" but primary component doesn't allow it
        if (
          component.quantityType === 'PER_PERSON' &&
          !this.canUsePerPersonForSecondary(component)
        ) {
          // Change to "FIXED" with quantity 1 as a safe default
          component.quantityType = 'FIXED';
          component.fixedQuantity = 1;
          console.log(
            `Fixed existing secondary component ${component.rateSet.public_name} from PER_PERSON to FIXED`,
          );
        }
      });
    },

    // Update secondary components quantity type based on primary component
    updateSecondaryComponentsQuantityType() {
      this.secondaryComponents.forEach((component) => {
        // If component is using "PER_PERSON" but primary component doesn't allow it
        if (
          component.quantityType === 'PER_PERSON' &&
          !this.canUsePerPersonForSecondary(component)
        ) {
          // Change to "FIXED" with quantity 1 as a safe default
          component.quantityType = 'FIXED';
          component.fixedQuantity = 1;
          console.log(
            `Updated secondary component ${component.rateSet.public_name} from PER_PERSON to FIXED`,
          );
        }
      });
    },

    // Method to handle when user tries to select a disabled option
    handleQuantityTypeChange(component, newValue) {
      // If trying to select PER_PERSON but it's not allowed, prevent it
      if (
        newValue === 'PER_PERSON' &&
        !this.canUsePerPersonForSecondary(component)
      ) {
        console.log('PER_PERSON option is disabled for this component');
        return; // Don't change the value
      }

      // Otherwise, allow the change
      component.quantityType = newValue;
    },

    updateSecondaryComponent(index, field, value) {
      if (this.secondaryComponents[index]) {
        this.secondaryComponents[index][field] = value;
        console.log(`Updated secondary component ${index}.${field}:`, value);
      }
    },

    // Toggle weekday for primary component discount condition
    togglePrimaryDiscountWeekday(dayIndex) {
      if (!this.primaryComponentConfig.discountWeekdays) {
        this.primaryComponentConfig.discountWeekdays = [];
      }
      const idx =
        this.primaryComponentConfig.discountWeekdays.indexOf(dayIndex);
      if (idx > -1) {
        this.primaryComponentConfig.discountWeekdays.splice(idx, 1);
      } else {
        this.primaryComponentConfig.discountWeekdays.push(dayIndex);
      }
    },

    // Form submission
    async submitPackage() {
      if (!this.validateAllSteps()) {
        console.error('Form validation failed');
        return;
      }

      this.isLoading = true;
      this.errors = {};

      try {
        const formData = this.buildFormData();

        const response = await fetch(
          '/calendars/business-settings/create-package/',
          {
            method: 'POST',
            body: formData,
            headers: {
              'X-CSRFToken': getCSRFToken(),
            },
          },
        );

        const result = await response.json();

        if (response.ok) {
          console.log('Package created successfully:', result);
          // Redirect back to calendar settings
          window.location.href = '/calendars/business-settings/';
        } else {
          this.errors = result.errors || {
            general: 'Failed to create package',
          };
          console.error('Package creation failed:', result);
        }
      } catch (error) {
        console.error('Error creating package:', error);
        this.errors = { general: 'An unexpected error occurred' };
      } finally {
        this.isLoading = false;
      }
    },

    buildFormData() {
      const formData = new FormData();

      // Basic package data
      formData.append('public_name', this.packageData.public_name);
      formData.append('short_description', this.packageData.short_description);
      formData.append('description', this.packageData.description);
      formData.append('features', this.packageData.features);
      formData.append('package_pricing_mode', this.pricingMode);

      // Primary component
      if (this.primaryComponent) {
        formData.append('primary_component_id', this.primaryComponent.id);
      }

      // Secondary components
      this.secondaryComponents.forEach((component, index) => {
        formData.append(
          `secondary_components[${index}][rate_set_id]`,
          component.rateSet.id,
        );
        formData.append(
          `secondary_components[${index}][exclude_days_from_start]`,
          component.excludeFromStart,
        );
        formData.append(
          `secondary_components[${index}][exclude_days_from_end]`,
          component.excludeFromEnd,
        );
        formData.append(
          `secondary_components[${index}][max_days]`,
          component.maxDays,
        );
        formData.append(
          `secondary_components[${index}][optional]`,
          component.optional,
        );
        formData.append(
          `secondary_components[${index}][quantity_type]`,
          component.quantityType,
        );

        // Weekdays as JSON array
        formData.append(
          `secondary_components[${index}][weekdays]`,
          JSON.stringify(component.weekdays),
        );
      });

      return formData;
    },

    validateAllSteps() {
      return (
        this.validateStep1() &&
        this.validateStep2() &&
        this.validateStep3() &&
        this.validateStep4()
      );
    },

    validateStep4() {
      // Step 4 (Preview) is always valid if we reach it
      this.stepValidation[4] = true;
      return true;
    },

    // Utility methods
    getStepTitle(step) {
      const titles = {
        1: 'Package Structure',
        2: 'Primary Component',
        3: 'Secondary Components',
        4: 'Preview',
      };
      return titles[step] || '';
    },

    getStepDescription(step) {
      const descriptions = {
        1: 'Set up your package name and choose how pricing will work.',
        2: 'Choose the main listing that will determine your package structure.',
        3: 'Add additional listings to your package with their configurations.',
        4: 'Review your package configuration before creating it.',
      };
      return descriptions[step] || '';
    },

    isStepComplete(step) {
      return this.stepValidation[step] || false;
    },

    getProgressPercentage() {
      return (this.currentStep / this.totalSteps) * 100;
    },

    async createPackage() {
      try {
        // Validate all steps are complete
        if (
          !this.isStepComplete(1) ||
          !this.isStepComplete(2) ||
          !this.isStepComplete(3)
        ) {
          throw new Error(
            'Please complete all steps before creating the package',
          );
        }

        // Prepare data for API
        const packageData = {
          packageData: {
            public_name: this.packageData.public_name,
            pricingMode: this.pricingMode,
            is_fixed: this.packageData.is_fixed || false,
          },
          primaryComponent: this.primaryComponent
            ? {
                id: this.primaryComponent.id,
                showsOnBreakdown: this.primaryComponentConfig.showsOnBreakdown,
                ignoreListingBlockedDates:
                  this.pricingMode === 'COMPONENT_SUM'
                    ? !!this.primaryComponentConfig.ignoreListingBlockedDates
                    : false,
                revenueAllocation:
                  this.primaryComponentConfig.revenueAllocation,
                pricingMode: this.primaryComponentConfig.pricingMode,
                // Only include discount fields if using discounted pricing
                ...(this.primaryComponentConfig.pricingMode ===
                'OWN_RATES_DISCOUNTED'
                  ? {
                      discountType:
                        this.primaryComponentConfig.discountType ||
                        'PERCENTAGE',
                      discount: this.primaryComponentConfig.discount || 0,
                      discountCondition:
                        this.primaryComponentConfig.discountCondition ||
                        'ALWAYS',
                      discountMinDays:
                        this.primaryComponentConfig.discountMinDays,
                      discountMaxDays:
                        this.primaryComponentConfig.discountMaxDays,
                      discountDaysBefore:
                        this.primaryComponentConfig.discountDaysBefore,
                      discountWeekdays:
                        this.primaryComponentConfig.discountWeekdays || [],
                    }
                  : {
                      discountType: 'PERCENTAGE', // Default values
                      discount: 0,
                    }),
                peopleInfluencePrice:
                  this.primaryComponentConfig.peopleInfluencePrice,
                usePersonTypes: this.primaryComponentConfig.usePersonTypes,
              }
            : null,
          secondaryComponents: this.secondaryComponents.map((component) => {
            this.normalizeSecondaryDateRules(component);
            const isRangeMode =
              (component.dateApplicationMode || 'RANGE') === 'RANGE';
            const maxDaysMode = isRangeMode
              ? component.maxDaysMode || 'FIXED'
              : 'FIXED';
            let maxDaysPrimaryDurationMap = {};
            if (isRangeMode && maxDaysMode === 'PRIMARY_DURATION_MAP') {
              const rows = component.maxDaysPrimaryDurationRows || [];
              const seenDays = new Set();
              for (const row of rows) {
                const primaryDaysRaw = row?.primaryDays;
                const maxDaysRaw = row?.maxDays;
                const hasPrimary =
                  primaryDaysRaw !== null &&
                  primaryDaysRaw !== undefined &&
                  primaryDaysRaw !== '';
                const hasMax =
                  maxDaysRaw !== null &&
                  maxDaysRaw !== undefined &&
                  maxDaysRaw !== '';

                // Ignore fully empty rows
                if (!hasPrimary && !hasMax) {
                  continue;
                }
                if (!hasPrimary || !hasMax) {
                  throw new Error(
                    `Please fill both Primary days and Max days for ${component.rateSet.public_name}.`,
                  );
                }

                const primaryDays = Number(primaryDaysRaw);
                const mappedMaxDays = Number(maxDaysRaw);

                if (!Number.isInteger(primaryDays) || primaryDays < 1) {
                  throw new Error(
                    `Primary days must be a whole number >= 1 for ${component.rateSet.public_name}.`,
                  );
                }
                if (!Number.isInteger(mappedMaxDays) || mappedMaxDays < 0) {
                  throw new Error(
                    `Max days must be a whole number >= 0 for ${component.rateSet.public_name}.`,
                  );
                }
                if (seenDays.has(primaryDays)) {
                  throw new Error(
                    `Duplicate primary days (${primaryDays}) in max days mapping for ${component.rateSet.public_name}.`,
                  );
                }

                seenDays.add(primaryDays);
                maxDaysPrimaryDurationMap[String(primaryDays)] = mappedMaxDays;
              }
            }

            return {
              rateSet: {
                id: component.rateSet.id,
              },
              showsOnBreakdown: component.showsOnBreakdown,
              ignoreListingBlockedDates:
                this.pricingMode === 'COMPONENT_SUM'
                  ? !!component.ignoreListingBlockedDates
                  : false,
              optional: component.optional,
              revenueAllocation: component.revenueAllocation,
              pricingMode: component.pricingMode,
              // Only include discount fields if using discounted pricing
              ...(component.pricingMode === 'OWN_RATES_DISCOUNTED'
                ? {
                    discountType: component.discountType || 'PERCENTAGE',
                    discount: component.discount || 0,
                    discountCondition: component.discountCondition || 'ALWAYS',
                    discountMinDays: component.discountMinDays,
                    discountMaxDays: component.discountMaxDays,
                    discountDaysBefore: component.discountDaysBefore,
                    discountWeekdays: component.discountWeekdays || [],
                  }
                : {
                    discountType: 'PERCENTAGE', // Default values
                    discount: 0,
                  }),
              quantityType: component.quantityType,
              fixedQuantity: component.fixedQuantity,
              minPrimaryIntervalDays: component.minPrimaryIntervalDays,
              excludeFromStart: isRangeMode ? component.excludeFromStart : 0,
              excludeFromEnd: isRangeMode ? component.excludeFromEnd : 0,
              maxDays: isRangeMode ? component.maxDays : 0,
              maxDaysMode: maxDaysMode,
              maxDaysPrimaryDurationMap: maxDaysPrimaryDurationMap,
              dateApplicationMode: component.dateApplicationMode || 'RANGE',
              addingDirection: isRangeMode
                ? component.addingDirection || 'FROM_START'
                : 'FROM_START',
              weekdayRestrictionActive: isRangeMode
                ? component.weekdayRestrictionActive
                : false,
              // Map UI days (0=Sunday..6=Saturday) to backend (0=Monday..6=Sunday)
              daysOfWeek: isRangeMode
                ? (component.daysOfWeek || []).map((d) => (d === 0 ? 6 : d - 1))
                : [],
            };
          }),
          secondaryExtras: this.secondaryExtras.map((component) => {
            return {
              extra: {
                id: component.extra.id,
              },
              quantityType: component.quantityType,
              fixedQuantity: component.fixedQuantity,
              maxQuantity: component.maxQuantity,
              minPrimaryIntervalDays: component.minPrimaryIntervalDays,
              durationAdjustment: component.durationAdjustment,
              revenueAllocation: component.revenueAllocation,
              pricingMode: component.pricingMode,
              discountType: component.discountType || 'PERCENTAGE',
              discountAmount:
                component.pricingMode === 'DISCOUNTED'
                  ? component.discountAmount || 0
                  : 0,
              showsOnBreakdown: component.showsOnBreakdown,
              displayName: component.displayName || '',
              active: component.active,
            };
          }),
        };

        // Get calendar ID from URL
        const urlParts = window.location.pathname.split('/');
        const calendarId = urlParts[urlParts.indexOf('business-settings') + 1];

        const response = await fetch(
          `/calendar/business-settings/${calendarId}/create-package/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': getCSRFToken(),
            },
            body: JSON.stringify(packageData),
          },
        );

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.error || 'Failed to create package');
        }

        return result;
      } catch (error) {
        console.error('Error creating package:', error);
        throw error;
      }
    },

    async createPackageAndShowSuccess() {
      try {
        if (this.hasCreated) return; // prevent duplicate submissions
        this.isLoading = true;
        this.errors = {};

        const result = await this.createPackage();

        // Store success data and show modal
        this.successData = result;
        this.showSuccessModal = false; // use confirmation step instead of modal
        this.hasCreated = true;
        this.createdPackageId = result.package_id || null;

        // Remove navigation warning since package is now created
        this.removeNavigationWarning();

        // Move to dedicated confirmation step and ensure it's visible
        this.currentStep = 5;
        // Clear form fields now that package is created
        this._resetFormFieldsAfterSuccess();
        // Ensure the confirmation section is visible to the user
        setTimeout(() => {
          const el = document.querySelector('[data-confirmation-step]');
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 0);
      } catch (error) {
        console.error('Error creating package:', error);
        this.errors = { general: error.message || 'Failed to create package' };
        alert(error.message || 'Failed to create package');
      } finally {
        this.isLoading = false;
      }
    },

    closeSuccessModal() {
      this.showSuccessModal = false;
      this.successData = null;
    },

    _resetFormFieldsAfterSuccess() {
      // Keep successData/createdPackageId/hasCreated and step 5, but clear form inputs
      this.packageData = { public_name: '', is_fixed: false };
      this.pricingMode = '';
      this.primaryComponent = null;
      this.primaryComponentConfig = {
        order: 0,
        showsOnBreakdown: false,
        ignoreListingBlockedDates: false,
        revenueAllocation: null,
        pricingMode: 'OWN_RATES',
        discountType: 'PERCENTAGE',
        discount: 0,
        discountCondition: 'ALWAYS',
        discountMinDays: null,
        discountMaxDays: null,
        discountDaysBefore: null,
        discountWeekdays: [],
        peopleInfluencePrice: false,
        usePersonTypes: false,
      };
      this.secondaryComponents = [];
      this.secondaryExtras = [];
      // Reset validations
      this.stepValidation = { 1: false, 2: false, 3: false, 4: false };
    },

    resetBuilder() {
      // Reset to initial state so user can create a new package
      this.currentStep = 1;
      this.isLoading = false;
      this.errors = {};
      this.showSuccessModal = false;
      this.successData = null;
      this.hasCreated = false;
      this.createdPackageId = null;
      this.packageData = { public_name: '', is_fixed: false };
      this.pricingMode = '';
      this.primaryComponent = null;
      this.primaryComponentConfig = {
        order: 0,
        showsOnBreakdown: false,
        ignoreListingBlockedDates: false,
        revenueAllocation: null,
        pricingMode: 'OWN_RATES',
        discountType: 'PERCENTAGE',
        discount: 0,
        discountCondition: 'ALWAYS',
        discountMinDays: null,
        discountMaxDays: null,
        discountDaysBefore: null,
        discountWeekdays: [],
        peopleInfluencePrice: false,
        usePersonTypes: false,
      };
      this.secondaryComponents = [];
      this.secondaryExtras = [];

      // Re-setup navigation warning for new package creation
      this.setupNavigationWarning();
    },
  });
}
